import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Login } from 'src/app/models/login';
import { ErrorHandlerService } from 'src/app/shared/error-handler/error-handler.service';
import { LoginResponses } from 'src/app/models/login-responses';
import { Router } from '@angular/router';
import { getAuth, signInWithPopup, signInWithRedirect, getRedirectResult, GoogleAuthProvider, FacebookAuthProvider, signOut, OAuthProvider, onAuthStateChanged } from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import { detectAuthRuntimeContext } from './auth-runtime-context';

type SocialLoginType = 'google' | 'facebook' | 'apple';

interface SocialLoginCredential {
  providerToken: string;
  loginType: SocialLoginType;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiUrl = environment.apiRoot;
  private clientId = environment.clientId;
  private userToken: string;
  userEmail$ = new Subject<string>();
  private userEmail: string;
  private returnUrl: string;
  public profileImg: string;
  returnUrl$ = new Subject<string>();
  authStateChanged$ = new Subject<boolean>();
  private readonly authRuntimeContext = detectAuthRuntimeContext(window as any);
  private auth = getAuth(initializeApp({
    ...environment.firebase,
    authDomain: this.authRuntimeContext === 'mobile-browser'
      ? environment.mobileBrowserAuthDomain
      : environment.firebase.authDomain
  }));

  constructor(
    private http: HttpClient,
    private ErrorHandlerService: ErrorHandlerService,
    private router: Router
  ) { }

  login(item: Login) {
    let body = new URLSearchParams();
    body.set('grant_type', 'password');
    body.set('client_id', this.clientId);
    body.set('username', item.email);
    body.set('password', item.password);

    let header = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    let option = { headers: header };

    return this.http
      .post<LoginResponses>(`${this.apiUrl}/token`, body, option)
      .pipe(
        tap((response) => {
          this.persistBackendSession(response);
        }),
        catchError((error) => {
          this.ErrorHandlerService.showServerError(error);
          return of(error);
        })
      );
  }

  // 🔹 Google Login
  async loginWithGoogle() {
    // Inside the Flutter webview PWA, social auth must be driven by the native app.
    // Running Firebase web SSO here opens Google's "continue in the app / use a secure
    // browser" page inside the WKWebView, which never completes. Hand off to native instead.
    if (this.triggerNativeLogin('signIn')) {
      return null;
    }
    try {
      const provider = new GoogleAuthProvider();
      // Force the account chooser so redirect login does not silently reuse the browser's active Google session.
      provider.setCustomParameters({ prompt: 'select_account' });
      if (this.shouldUseRedirectSocialLogin()) {
        this.setPendingSocialLogin('google');
        await signInWithRedirect(this.auth, provider);
        return null;
      }

      const result = await signInWithPopup(this.auth, provider);
      return await this.createSocialLoginCredential(result, 'google');
    } catch (error) {
      console.error('Google login failed:', error);
      this.clearPendingSocialLogin();
      return null;
    }
  }

  private setPendingSocialLogin(loginType: string) {
    sessionStorage.setItem('pendingSocialLoginType', loginType);
    sessionStorage.setItem('pendingSocialReturnUrl', this.returnUrl || this.router.url || '/');
    localStorage.setItem('pendingSocialLoginType', loginType);
    localStorage.setItem('pendingSocialReturnUrl', this.returnUrl || this.router.url || '/');
  }

  private getPendingSocialLoginType(): string {
    return sessionStorage.getItem('pendingSocialLoginType') || localStorage.getItem('pendingSocialLoginType') || '';
  }

  private clearPendingSocialLogin() {
    sessionStorage.removeItem('pendingSocialLoginType');
    localStorage.removeItem('pendingSocialLoginType');
  }

  private shouldUseRedirectSocialLogin(): boolean {
    return this.authRuntimeContext === 'mobile-browser';
  }

  // True only when running inside the Flutter app's webview (native login bridge present).
  // A plain mobile browser / installed PWA has no bridge and returns false here, so it falls
  // through to the Firebase web SSO flow instead of being blocked.
  private triggerNativeLogin(type: 'signIn' | 'signUp' = 'signIn'): boolean {
    const win = window as any;
    const handler = type === 'signUp' ? win.SignUpHandler : win.SignInHandler;
    if (!handler?.postMessage) {
      return false;
    }
    handler.postMessage(type);
    return true;
  }

  async getRedirectSocialLoginResult(): Promise<SocialLoginCredential | null> {
    // Redirect completion belongs only to a normal mobile browser/PWA. Desktop keeps
    // the proven popup result path, and the Flutter MobileView owns its native session.
    if (this.authRuntimeContext !== 'mobile-browser') {
      return null;
    }

    const pendingLoginType = this.getPendingSocialLoginType();

    try {
      const result = await getRedirectResult(this.auth);
      // Some mobile browsers restore Firebase's signed-in user before (or without)
      // returning a UserCredential from getRedirectResult(). The pending marker proves
      // this page load is completing a social redirect, so allow that restored user to
      // continue through the same Firebase-ID-token/backend flow as the desktop popup.
      const redirectUser = result?.user || (pendingLoginType ? await this.waitForCurrentFirebaseUser() : null);

      if (!redirectUser) {
        if (pendingLoginType) {
          this.clearPendingSocialLogin();
        }
        return null;
      }

      const loginType = pendingLoginType || this.getLoginTypeFromRedirectResult(result);
      if (!this.isSocialLoginType(loginType)) {
        this.clearPendingSocialLogin();
        return null;
      }

      const credential = await this.createSocialLoginCredential(result || { user: redirectUser }, loginType);
      this.clearPendingSocialLogin();
      return credential;
    } catch (error) {
      console.error('Social redirect login failed:', error);
      this.clearPendingSocialLogin();
      return null;
    }
  }

  private waitForCurrentFirebaseUser(timeoutMs = 5000): Promise<any | null> {
    if (this.auth.currentUser) {
      return Promise.resolve(this.auth.currentUser);
    }

    return new Promise((resolve) => {
      let settled = false;
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      let unsubscribe: (() => void) | null = null;

      const finish = (user: any | null) => {
        if (settled) {
          return;
        }
        settled = true;
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (unsubscribe) {
          unsubscribe();
        }
        resolve(user);
      };

      timeoutId = setTimeout(() => finish(this.auth.currentUser || null), timeoutMs);
      unsubscribe = onAuthStateChanged(this.auth, (user) => {
        if (user) {
          finish(user);
        }
      });

      if (settled && unsubscribe) {
        unsubscribe();
      }
    });
  }

  consumeSocialLoginReturnUrl(): string {
    const returnUrl = sessionStorage.getItem('pendingSocialReturnUrl') || localStorage.getItem('pendingSocialReturnUrl') || '';
    sessionStorage.removeItem('pendingSocialReturnUrl');
    localStorage.removeItem('pendingSocialReturnUrl');
    return returnUrl;
  }

  // 🔹 Facebook Login
  async loginWithFacebook() {
    if (this.triggerNativeLogin('signIn')) {
      return null;
    }
    try {
      const provider = new FacebookAuthProvider();
      if (this.shouldUseRedirectSocialLogin()) {
        this.setPendingSocialLogin('facebook');
        await signInWithRedirect(this.auth, provider);
        return null;
      }

      const result = await signInWithPopup(this.auth, provider);
      return await this.createSocialLoginCredential(result, 'facebook');
    } catch (error) {
      console.log('Facebook login failed:', error);
      this.clearPendingSocialLogin();
      return null;
    }
  }

  // 🔹 Apple Login
  async loginWithApple() {
    if (this.triggerNativeLogin('signIn')) {
      return null;
    }
    try {
      const provider = new OAuthProvider("apple.com");
      if (this.shouldUseRedirectSocialLogin()) {
        this.setPendingSocialLogin('apple');
        await signInWithRedirect(this.auth, provider);
        return null;
      }

      const result = await signInWithPopup(this.auth, provider);
      return await this.createSocialLoginCredential(result, 'apple');
    } catch (error) {
      console.log('Apple login failed:', error);
      this.clearPendingSocialLogin();
      return null;
    }
  }

  private async createSocialLoginCredential(result: any, loginType: SocialLoginType): Promise<SocialLoginCredential> {
    const providerToken = await this.getFirebaseIdToken(result);
    if (!providerToken) {
      throw new Error('Firebase did not return a usable ' + loginType + ' login token.');
    }

    return { providerToken, loginType };
  }

  // The ERP /token social_login grant verifies this value with Firebase's VerifyIdTokenAsync,
  // so it must be the signed-in user's Firebase ID token — sending the Google/Facebook/Apple
  // provider credential instead fails backend verification and the login silently dies.
  private async getFirebaseIdToken(result: any): Promise<string> {
    const user = result?.user;
    if (!user) {
      return '';
    }

    if (typeof user.getIdToken === 'function') {
      try {
        const token = await user.getIdToken();
        if (token) {
          return token;
        }
      } catch {
        // fall through to the cached token fields below
      }
    }

    return user.accessToken || user.stsTokenManager?.accessToken || '';
  }

  private isSocialLoginType(loginType: string): loginType is SocialLoginType {
    return loginType === 'google' || loginType === 'facebook' || loginType === 'apple';
  }

  private getLoginTypeFromRedirectResult(result: any): SocialLoginType | '' {
    const providerId = result?.providerId || result?.user?.providerData?.[0]?.providerId || '';
    if (providerId === 'google.com') {
      return 'google';
    }
    if (providerId === 'facebook.com') {
      return 'facebook';
    }
    if (providerId === 'apple.com') {
      return 'apple';
    }
    return '';
  }

  sendTokenToBackend(providerToken: string, loginType: string) {
    let body = new URLSearchParams();
    body.set('grant_type', 'social_login');
    body.set('client_id', this.clientId);
    body.set('code', providerToken);
    body.set('loginType', loginType);

    let header = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    let option = { headers: header };

    return this.http
      .post<LoginResponses>(`${this.apiUrl}/token`, body, option)
      .pipe(
        tap((response) => {
          // This shared handoff is used by both desktop popup and mobile redirect.
          // Persist before subscriber UI work so a reload/header check immediately sees
          // the authenticated session after the backend accepts the Firebase ID token.
          this.persistBackendSession(response);
        }),
        catchError((error) => {
          this.ErrorHandlerService.showServerError(error);
          return of(error);
        })
      );
  }

  private persistBackendSession(response: LoginResponses): void {
    this.userToken = response?.access_token || '';
    if (!response?.access_token) {
      return;
    }

    localStorage.setItem('token', response.access_token);
    localStorage.setItem('refreshToken', response.refresh_token || '');
    localStorage.setItem('expiresIn', response.expires_in || '');
    this.authStateChanged$.next(true);
  }

  loginWithInstagram() {
    const authUrl = `https://api.instagram.com/oauth/authorize?client_id=${environment.instaClientId}&redirect_uri=${environment.instaRedirectURL}&response_type=code&scope=user_profile,email`;
    window.location.href = authUrl;
  }

  refreshToken(): Observable<any> {
    // Simulate refresh token API call
    return this.http.post<any>(`${this.apiUrl}/refresh-token`, {}).pipe(
      tap((response) => {
        // Assuming the API returns new user roles in the response
        this.userToken = response.roles || '';
      }),
      catchError((error) => {
        console.error('Refresh token failed', error);
        return of(null);
      })
    );
  }

  isAuthenticated(): boolean {
    return this.getToken() != '' ? true : false;
  }

  getToken(): string {
    if (localStorage.getItem('token')) {
      var token = localStorage.getItem('token') || '';
      return token;
    } else return '';
  }

  getOTP(email: string): Observable<string> {
    return this.http
      .get<string>(`${this.apiUrl}/api/password/reset/` + email + `/request`)
      .pipe(
        tap((response) => { }),
        catchError((error) => {
          this.ErrorHandlerService.showServerError(error);
          return of(error);
        })
      );
  }

  verifyOTP(code: string) {
    return this.http
      .get<any>(
        `${this.apiUrl}/api/tenants/membershipApp/ValidateMembershipResetPasswordInvitationCode/` +
        code
      )
      .pipe(
        tap((response) => { }),
        catchError((error) => {
          this.ErrorHandlerService.showServerError(error);
          return of(error);
        })
      );
  }

  updatePassword(item: Login) {
    return this.http
      .post<string>(`${this.apiUrl}/api/resetpassword`, item)
      .pipe(
        tap(() => { }),
        catchError((error) => {
          this.ErrorHandlerService.showServerError(error);
          return of(error);
        })
      );
  }

  updateEmail(email: string) {
    this.userEmail = email;
    this.userEmail$.next(email);
  }

  getEmail(): string {
    return this.userEmail;
  }

  updateReturnURL(url: string) {
    this.returnUrl = url;
    this.returnUrl$.next(url);
  }

  getReturnURL(): string {
    return this.returnUrl;
  }

  logout() {
    localStorage.clear();
    this.userToken = '';
    this.userEmail = '';
    this.profileImg = '';
    signOut(this.auth);
    // Emit auth state change
    this.authStateChanged$.next(false);
    // Navigate to home page
    this.router.navigate(['/']);
  }

  // Helper method to check if user is logged in
  isLoggedIn(): boolean {
    return !!this.getToken();
  }
}
